import { useMutation } from '@apollo/client'
import { useState } from 'react'

import useMsgs from '@/gf/hooks/useMsgs'
import MoneyM from '@/gf/modules/Money'
import { TvhPartsQuery } from '@/suppliers/_gen/gql'
import ADD_CUSTOM_PRODUCTS from '@/suppliers/hooks/useAddCustomProducts'

import Action from '@/gf/components/Action'
import Form from '@/gf/components/Form'
import PriceInput from '@/gf/components/inputs/PriceV2'
import QuantityInput from '@/gf/components/inputs/Quantity'
import Label from '@/gf/components/Label'

import type { LineItemPayload, Money } from '@/types'

export type TvhPart = TvhPartsQuery['tvhParts'][number]
type Fields = { quantity: number; price: Money | null }

const TvhPart = ({
  part,
  orgId,
  storeId,
  onAdded,
}: {
  part: TvhPart
  orgId?: string
  storeId?: string
  onAdded: (lineItems: LineItemPayload[]) => void
}) => {
  const [_, msgr] = useMsgs()
  const initFields = { quantity: 1, price: null }
  const [fields, setFields] = useState<Fields>(initFields)
  const [adding, setAdding] = useState(false)
  const [addProducts] = useMutation(ADD_CUSTOM_PRODUCTS)

  const onAdd = () => {
    if (!fields.price || !storeId) {
      msgr.add('Store and price are required.', 'neutral')
      return
    }

    setAdding(true)

    addProducts({
      variables: {
        organizationId: orgId,
        storeId,
        partRequests: [
          {
            name: part.name,
            mpn: part.mpn,
            quantity: fields.quantity,
            salePrice: MoneyM.toString(fields.price),
          },
        ],
      },
    })
      .then(({ data }) => {
        setFields(initFields)
        onAdded(data?.addCustomProducts)
        msgr.add('Product added to quote.', 'positive')
      })
      .finally(() => setAdding(false))
  }

  return (
    <div className="space-y-2 py-4 first:pt-0 last:pb-0" key={part.mpn}>
      <div className="font-bold">{part.name}</div>
      <div>{part.mpn}</div>
      <div dangerouslySetInnerHTML={{ __html: part.description }} />

      <div className="flex gap-4 whitespace-nowrap">
        {part.priceBreaks.map((pb) => (
          <div>
            {pb.quantity} @ {MoneyM.format(pb.price)}
          </div>
        ))}
      </div>

      <Form onSubmit={onAdd}>
        <div className="flex gap-4 items-center">
          <div className="flex gap-2 items-center">
            <Label>Quantity</Label>
            <div className="w-20">
              <QuantityInput
                min={1}
                value={fields.quantity}
                onChange={({ target }) => setFields({ ...fields, quantity: target.valueAsNumber })}
              />
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <Label>Price</Label>
            <div className=" w-32">
              <PriceInput
                price={fields.price}
                onChange={(price) => setFields({ ...fields, price: price || null })}
                min={0.01}
              />
            </div>
          </div>

          <Action.S type="submit" performing={adding}>
            Add
          </Action.S>
        </div>
      </Form>
    </div>
  )
}

export default TvhPart
